import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Calculator,
  Calendar,
  Gauge,
  Fuel,
  Cog,
  Zap,
  Leaf,
  Car,
  Droplet,
  Palette,
  Check,
  ChevronLeft,
  ChevronRight,
  X,
  ShieldCheck,
  Wallet,
  Repeat2,
  Truck,
  MapPin,
  BookmarkPlus,
  Send,
  ArrowUpRight,
  Sparkles,
  Ruler,
  MousePointerClick,
  Maximize2,
} from "lucide-react";
import api, { assetUrl, formatApiError } from "@/lib/api";
import CarCard from "@/components/site/CarCard";
import SmartImage from "@/components/site/SmartImage";
import Seo from "@/components/site/Seo";
import { formatPrice, formatKm } from "@/lib/format";
import { useSettings } from "@/lib/settings";
import { TID } from "@/constants/tids";
import { toast } from "sonner";

/* ---------- Feature categorization (client-side, keyword-based) ---------- */
const CATEGORY_ORDER = [
  "Confort",
  "Siguranță",
  "Exterior",
  "Interior",
  "Multimedia",
  "Asistență",
  "Alte dotări",
];

const CATEGORY_KEYWORDS = {
  Confort: [
    "climatronic",
    "climatizare",
    "aer condiționat",
    "aer conditionat",
    "scaune încălzite",
    "incalzite",
    "încălzire",
    "volan încălzit",
    "acces fără cheie",
    "keyless",
    "start stop",
    "pornire buton",
  ],
  Siguranță: [
    "abs",
    "esp",
    "airbag",
    "cameră",
    "camera",
    "senzori",
    "distronic",
    "frânare",
    "franare",
    "frana",
    "collision",
    "coliziune",
    "asistență",
    "asistenta",
    "isofix",
    "control tracțiune",
    "control stabilitate",
    "toyota safety",
    "hold",
  ],
  Exterior: [
    "faruri",
    "led",
    "xenon",
    "matrix",
    "multibeam",
    "jante",
    "trapa",
    "trapă",
    "acoperiș",
    "acoperis",
    "spoiler",
    "hayon",
    "oglinzi",
    "cârlig",
    "carlig",
  ],
  Interior: [
    "piele",
    "artico",
    "alcantara",
    "widescreen",
    "cockpit",
    "ambientală",
    "ambientala",
    "iluminare",
    "cotieră",
    "cotiera",
    "scaune",
    "suede",
  ],
  Multimedia: [
    "navigație",
    "navigatie",
    "mmi",
    "mbux",
    "bluetooth",
    "carplay",
    "android auto",
    "ecran",
    "display",
    "sunet",
    "boxe",
    "sound",
    "usb",
  ],
  Asistență: [
    "cruise",
    "adaptiv",
    "lane",
    "banda",
    "bandă",
    "park assist",
    "asistent",
    "hill",
    "traffic",
    "sign",
  ],
};

function categorizeFeatures(features = []) {
  const groups = Object.fromEntries(CATEGORY_ORDER.map((c) => [c, []]));
  for (const f of features) {
    const low = String(f).toLowerCase();
    let placed = false;
    for (const cat of CATEGORY_ORDER) {
      if (cat === "Alte dotări") continue;
      const kws = CATEGORY_KEYWORDS[cat];
      if (kws && kws.some((kw) => low.includes(kw))) {
        groups[cat].push(f);
        placed = true;
        break;
      }
    }
    if (!placed) groups["Alte dotări"].push(f);
  }
  return CATEGORY_ORDER.map((c) => ({ category: c, items: groups[c] })).filter(
    (g) => g.items.length > 0,
  );
}

/* ---------- Small primitives ---------- */
const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Verificată tehnic" },
  { icon: Check, label: "Garanție inclusă" },
  { icon: Wallet, label: "Posibilitate finanțare" },
  { icon: Repeat2, label: "Buy Back" },
];

const AUTORIO_BENEFITS = [
  { icon: ShieldCheck, text: "Garanție 12 luni", sub: "prelungibilă până la 3 ani" },
  { icon: Wallet, text: "Posibilitate finanțare", sub: "rapidă doar cu buletinul" },
  { icon: Repeat2, text: "Serviciu Buy Back", sub: "răscumpărăm mașina ta" },
  { icon: ShieldCheck, text: "Verificare tehnică", sub: "efectuată de specialiști" },
  { icon: Truck, text: "Livrare în toată țara", sub: "acasă la tine" },
  { icon: MapPin, text: "Consiliere înmatriculare", sub: "gratuit, fără stres" },
];

function SpecCard({ icon: Icon, label, val }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#111111] p-4 flex items-center gap-3 hover:border-[#D4AF37]/30 transition-colors">
      <span className="h-10 w-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#D4AF37]" strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-bold">
          {label}
        </div>
        <div className="text-[15px] font-bold text-white truncate">{val}</div>
      </div>
    </div>
  );
}

function PillBadge({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.08] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]">
      <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
      {children}
    </span>
  );
}

/* ---------- Rate calculator ---------- */
function computeMonthlyRate(price, advance, months, annualRate = 8.5) {
  const P = Math.max(0, price - (advance || 0));
  if (P <= 0 || !months) return 0;
  const r = annualRate / 100 / 12;
  if (r === 0) return P / months;
  return (P * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

/* ---------- Main component ---------- */
export default function CarDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const [car, setCar] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formIntent, setFormIntent] = useState("finantare"); // 'finantare' | 'rezervare' | 'mesaj'
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    advance: "",
    period_months: 60,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setCar(null);
    api
      .get(`/vehicles/${slug}`)
      .then((r) => {
        setCar(r.data);
        setIdx(0);
      })
      .catch(() => nav("/masini"));
    api
      .get(`/vehicles/${slug}/similar`, { params: { limit: 3 } })
      .then((r) => setSimilar(r.data))
      .catch(() => { });
    window.scrollTo(0, 0);
  }, [slug, nav]);

  /* keyboard nav */
  useEffect(() => {
    const onKey = (e) => {
      if (!car) return;
      if (fs) {
        if (e.key === "Escape") setFs(false);
        if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + imgs.length) % imgs.length);
        if (e.key === "ArrowRight") setIdx((i) => (i + 1) % imgs.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fs, car]);

  const imgs = useMemo(
    () =>
      car?.images && car.images.length > 0
        ? car.images.map(assetUrl)
        : ["https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200"],
    [car],
  );

  const featureGroups = useMemo(
    () => (car ? categorizeFeatures(car.features || []) : []),
    [car],
  );

  const engineLine =
    car && (car.power_hp || car.engine_size)
      ? `${car.engine_size ? car.engine_size.toFixed(1) + "L" : ""}${car.engine_size && car.power_hp ? " · " : ""
      }${car.power_hp ? car.power_hp + " CP" : ""}`
      : "—";

  const specs = car
    ? [
      { icon: Zap, label: "Motorizare", val: engineLine },
      { icon: Fuel, label: "Combustibil", val: car.fuel },
      { icon: Cog, label: "Cutie", val: car.transmission },
      { icon: Calendar, label: "An fabricație", val: car.year },
      { icon: Gauge, label: "Kilometri", val: formatKm(car.mileage) },
      { icon: Car, label: "Caroserie", val: car.body_type },
      {
        icon: Zap,
        label: "Putere",
        val: car.power_hp ? `${car.power_hp} CP` : "—",
      },
      { icon: Cog, label: "Tracțiune", val: car.drivetrain || "—" },
      { icon: Leaf, label: "Normă Euro", val: car.emission_standard || "—" },
      {
        icon: Ruler,
        label: "Capacitate",
        val: car.engine_size ? `${car.engine_size} L` : "—",
      },
      {
        icon: Droplet,
        label: "Consum",
        val: car.consumption ? `${car.consumption} L/100km` : "—",
      },
      { icon: Palette, label: "Culoare", val: car.color || "—" },
    ]
    : [];

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        type: "masina",
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message
          ? `[${formIntent.toUpperCase()}] ${form.message}`
          : `[${formIntent.toUpperCase()}]`,
        advance: form.advance ? parseFloat(form.advance) : null,
        period_months: form.period_months ? parseInt(form.period_months) : null,
        vehicle_id: car.id,
      });
      toast.success("Cererea a fost trimisă. Te contactăm în cel mai scurt timp.");
      setShowForm(false);
      setForm({
        name: "",
        phone: "",
        email: "",
        advance: "",
        period_months: 60,
        message: "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openForm = (intent) => {
    setFormIntent(intent);
    setShowForm(true);
  };

  if (!car) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen pt-32 pb-20">
        <div className="container-wide">
          <div className="animate-pulse bg-white/[0.03] h-[500px] rounded-3xl border border-white/[0.05]" />
        </div>
      </div>
    );
  }

  const phoneClean = settings.phone.replace(/\s/g, "");
  const wa = settings.whatsapp.replace(/\s/g, "");
  const waText = encodeURIComponent(
    `Salut! Sunt interesat de ${car.brand} ${car.model} (${car.year}) — ${window.location.href}`,
  );

  const carJsonLd = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    name: `${car.brand} ${car.model} ${car.year}`,
    brand: { "@type": "Brand", name: car.brand },
    model: car.model,
    vehicleModelDate: String(car.year),
    mileageFromOdometer: {
      "@type": "QuantitativeValue",
      value: car.mileage,
      unitCode: "KMT",
    },
    fuelType: car.fuel,
    vehicleTransmission: car.transmission,
    bodyType: car.body_type,
    ...(car.color ? { color: car.color } : {}),
    image: imgs,
    offers: {
      "@type": "Offer",
      price: car.price,
      priceCurrency: "EUR",
      itemCondition: "https://schema.org/UsedCondition",
      availability:
        car.status === "vanduta"
          ? "https://schema.org/SoldOut"
          : "https://schema.org/InStock",
      seller: {
        "@type": "AutoDealer",
        name: "AutoRIO",
        areaServed: "Suceava",
      },
    },
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen pb-28 lg:pb-16">
      <Seo
        title={`${car.brand} ${car.model} ${car.year} · ${formatPrice(car.price)} | AutoRIO Suceava`}
        description={`${car.brand} ${car.model} din ${car.year}, ${formatKm(car.mileage)}, ${car.fuel}, ${car.transmission}. ${car.body_type} second hand verificat, de vânzare la AutoRIO Suceava.`}
        image={imgs[0]}
        type="product"
        jsonLd={carJsonLd}
      />

      {/* Back link */}
      <div className="container-wide pt-28 sm:pt-32">
        <Link
          to="/masini"
          className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-[#D4AF37] transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Înapoi la stoc
        </Link>
      </div>

      {/* ---------- Hero title ---------- */}
      <section className="container-wide pt-6 sm:pt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
            {car.brand}
          </div>
          <h1 className="mt-3 font-display text-3xl sm:text-5xl lg:text-[56px] font-extrabold text-white leading-[1.02] tracking-tight">
            {car.model}{" "}
            <span className="font-serif-italic gold-gradient-text font-medium">
              {car.year}
            </span>
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-white/70 text-[15px]">
            <span className="inline-flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-[#D4AF37]" />
              {engineLine}
            </span>
            <span className="text-white/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Fuel className="h-4 w-4 text-[#D4AF37]" />
              {car.fuel}
            </span>
            <span className="text-white/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Gauge className="h-4 w-4 text-[#D4AF37]" />
              {formatKm(car.mileage)}
            </span>
            <span className="text-white/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Car className="h-4 w-4 text-[#D4AF37]" />
              {car.body_type}
            </span>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {TRUST_BADGES.map((b) => (
              <PillBadge key={b.label} icon={b.icon}>
                {b.label}
              </PillBadge>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ---------- Main grid: gallery/details + sticky sidebar ---------- */}
      <div className="container-wide mt-10 sm:mt-12 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 lg:gap-10">
        <div>
          {/* Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="rounded-3xl overflow-hidden border border-white/[0.06] bg-[#111111] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.9)]"
          >
            <div
              className="relative w-full aspect-[16/10] bg-black overflow-hidden cursor-zoom-in group lg:aspect-[16/9] xl:aspect-[16/8.5] 2xl:aspect-[16/8]"
              onClick={() => setFs(true)}
            >
              <SmartImage
                src={imgs[idx]}
                context="full"
                alt={`${car.brand} ${car.model}`}
                className={`w-full h-full object-cover transition-transform duration-[700ms] ease-out ${zoomed ? "scale-125" : "group-hover:scale-[1.03]"
                  }`}
                eager
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

              {/* Zoom + fullscreen hint */}
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomed((z) => !z);
                  }}
                  className="h-10 w-10 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white hover:bg-[#D4AF37] hover:text-[#0a0a0a] hover:border-[#D4AF37] transition-colors flex items-center justify-center"
                  aria-label="Zoom"
                >
                  <MousePointerClick className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFs(true);
                  }}
                  className="h-10 w-10 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white hover:bg-[#D4AF37] hover:text-[#0a0a0a] hover:border-[#D4AF37] transition-colors flex items-center justify-center"
                  aria-label="Ecran complet"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>

              {imgs.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdx((i) => (i - 1 + imgs.length) % imgs.length);
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white hover:bg-[#D4AF37] hover:text-[#0a0a0a] hover:border-[#D4AF37] transition-colors flex items-center justify-center"
                    aria-label="Anterior"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdx((i) => (i + 1) % imgs.length);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white hover:bg-[#D4AF37] hover:text-[#0a0a0a] hover:border-[#D4AF37] transition-colors flex items-center justify-center"
                    aria-label="Următor"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}

              {/* Counter */}
              <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur border border-white/15 rounded-full px-3 py-1.5 text-[11px] font-bold text-white">
                {idx + 1} / {imgs.length}
              </div>
            </div>

          </motion.div>

          {/* {imgs.length > 1 && (
              <div className="p-4 flex gap-2.5 overflow-x-auto no-scrollbar">
                {imgs.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Vezi imaginea ${i + 1}`}
                    className={`shrink-0 h-16 w-24 sm:h-20 sm:w-28 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      i === idx
                        ? "border-[#D4AF37] opacity-100 scale-100"
                        : "border-transparent opacity-60 hover:opacity-100 hover:scale-[1.04]"
                    }`}
                  >
                    <SmartImage
                      src={src}
                      context="card"
                      className="w-full h-full object-cover"
                      alt=""
                    />
                  </button>
                ))}
              </div>
            )} */ }

          {/* Mobile inline price */}
          <div className="lg:hidden mt-6 relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#141210] via-[#111] to-[#0a0a0a] border border-white/[0.06] p-6">
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#D4AF37]/15 blur-3xl" />
            <div className="relative flex items-baseline justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold">
                  Preț
                </div>
                <div className="font-display text-4xl font-extrabold text-[#D4AF37] leading-none mt-1">
                  {formatPrice(car.price)}
                </div>
              </div>
              <div className="text-right text-[11px] text-white/50 leading-relaxed">
                TVA inclus
                <br />
                Livrare RO
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              <PillBadge icon={Wallet}>Posibilitate finanțare</PillBadge>
              <PillBadge icon={ShieldCheck}>Garanție inclusă</PillBadge>
            </div>
          </div>

          {/* Specs */}
          <section className="mt-12 sm:mt-16">
            <div className="mb-6">
              <div className="eyebrow">Specificații tehnice</div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Fișa completă a mașinii
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {specs.map((s) => (
                <SpecCard key={s.label} {...s} />
              ))}
            </div>
          </section>

          {/* Description */}
          {car.description && (
            <section className="mt-12 sm:mt-16">
              <div className="mb-6">
                <div className="eyebrow">Prezentare</div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Descrierea vehiculului
                </h2>
              </div>
              <div className="rounded-2xl border border-white/[0.06] bg-[#111111] p-6 sm:p-8">
                <p className="text-white/70 leading-[1.85] text-[15px] whitespace-pre-line">
                  {car.description}
                </p>
              </div>
            </section>
          )}

          {/* Features grouped */}
          {featureGroups.length > 0 && (
            <section className="mt-12 sm:mt-16">
              <div className="mb-6">
                <div className="eyebrow">Echipare</div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Dotări incluse
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {featureGroups.map((g, gi) => (
                  <motion.div
                    key={g.category}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: gi * 0.06 }}
                    className="rounded-2xl border border-white/[0.06] bg-[#111111] p-6"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
                        {g.category}
                      </div>
                    </div>
                    <ul className="space-y-2.5">
                      {g.items.map((f) => (
                        <li
                          key={f}
                          className="flex items-start gap-2.5 text-[14px] text-white/80"
                        >
                          <Check
                            className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0"
                            strokeWidth={2.5}
                          />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* AutoRIO benefits */}
          <section className="mt-12 sm:mt-16">
            <div className="relative rounded-3xl overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#111] to-[#0a0a0a] p-6 sm:p-10">
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#D4AF37]/12 blur-3xl" />
              <div className="relative">
                <div className="eyebrow">Cu încredere</div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  De ce să alegi{" "}
                  <span className="font-serif-italic gold-gradient-text font-medium">
                    AutoRIO
                  </span>
                  ?
                </h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {AUTORIO_BENEFITS.map((b) => (
                    <div key={b.text} className="flex items-start gap-3">
                      <span className="mt-0.5 h-9 w-9 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                        <b.icon
                          className="h-4 w-4 text-[#D4AF37]"
                          strokeWidth={2.2}
                        />
                      </span>
                      <div>
                        <div className="text-white font-bold text-[15px] leading-tight">
                          {b.text}
                        </div>
                        <div className="text-white/50 text-[13px] mt-0.5">
                          {b.sub}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Rate calculator */}
          <section className="mt-12 sm:mt-16">
            <RateCalculator
              price={car.price}
              onRequest={() => openForm("finantare")}
            />
          </section>

          {/* Finanțare TBI Bank */}
          <section className="mt-12 sm:mt-16">
            <div className="relative rounded-3xl overflow-hidden border border-white/[0.06] bg-[#111111] p-6 sm:p-8">
              <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-[#D4AF37]/10 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
                  <div className="eyebrow !text-[#D4AF37]">Partener financiar</div>
                </div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Finanțare prin{" "}
                  <span className="font-serif-italic gold-gradient-text font-medium">
                    TBI Bank
                  </span>
                </h2>
                <p className="mt-2 text-white/50 text-sm max-w-xl">
                  Achiziționează această mașină prin finanțare TBI Bank. Pentru
                  detalii și solicitarea unei finanțări, accesează pagina
                  dedicată AutoRIO.
                </p>
                <a
                  data-testid="car-detail-tbi-financing"
                  href="https://tbibank.ro/lcp-odi-mega-trans-srl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-12 px-7 font-bold text-[12px] uppercase tracking-[0.16em] transition-colors shadow-[0_10px_30px_-8px_rgba(212,175,55,0.5)]"
                >
                  Solicită finanțare prin TBI Bank
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </section>
        </div>

        {/* ---------- Sticky sidebar ---------- */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-4">
            {/* Price card */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#141210] via-[#111] to-[#0a0a0a] border border-white/[0.08] p-7 shadow-[0_25px_70px_-20px_rgba(0,0,0,0.9)]">
              <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-[#D4AF37]/18 blur-3xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold">
                  Preț
                </div>
                <div className="mt-2 font-display text-5xl font-extrabold text-[#D4AF37] leading-none tracking-tight">
                  {formatPrice(car.price)}
                </div>
                <div className="mt-2 text-[12px] text-white/50">
                  TVA inclus · Livrare în toată România
                </div>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  <PillBadge icon={Wallet}>Finanțare</PillBadge>
                  <PillBadge icon={ShieldCheck}>Garanție inclusă</PillBadge>
                </div>

                <div className="mt-6 space-y-2.5">
                  <a
                    data-testid={TID.carDetailPhone}
                    href={`tel:${phoneClean}`}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-12 font-bold text-[13px] uppercase tracking-[0.14em] transition-colors shadow-[0_10px_30px_-8px_rgba(212,175,55,0.5)]"
                  >
                    <Phone className="h-4 w-4" />
                    Sună · {settings.phone}
                  </a>
                  {settings.phone_secondary && (
                    <a
                      data-testid="car-detail-phone-alt"
                      href={`tel:${settings.phone_secondary.replace(/\s/g, "")}`}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-12 font-bold text-[13px] uppercase tracking-[0.14em] transition-colors shadow-[0_10px_30px_-8px_rgba(212,175,55,0.5)]"
                    >
                      <Phone className="h-4 w-4" />
                      Sună · {settings.phone_secondary}
                    </a>
                  )}
                  <a
                    data-testid={TID.carDetailWhatsapp}
                    href={`https://wa.me/40${wa.replace(/^0/, "")}?text=${waText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full h-12 font-bold text-[13px] uppercase tracking-[0.14em] transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </a>
                  <button
                    data-testid={TID.carDetailFinancing}
                    onClick={() => openForm("finantare")}
                    className="w-full inline-flex items-center justify-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.1] hover:border-[#D4AF37]/50 text-white rounded-full h-12 font-bold text-[13px] uppercase tracking-[0.14em] transition-colors"
                  >
                    <Calculator className="h-4 w-4 text-[#D4AF37]" />
                    Solicită ofertă
                  </button>
                  <button
                    onClick={() => openForm("rezervare")}
                    className="w-full inline-flex items-center justify-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.1] hover:border-[#D4AF37]/50 text-white rounded-full h-12 font-bold text-[13px] uppercase tracking-[0.14em] transition-colors"
                  >
                    <BookmarkPlus className="h-4 w-4 text-[#D4AF37]" />
                    Rezervă mașina
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-2.5 text-[13px] text-white/65">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#D4AF37]" strokeWidth={2.5} />
                    Kilometri verificați
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#D4AF37]" strokeWidth={2.5} />
                    Verificare tehnică efectuată
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#D4AF37]" strokeWidth={2.5} />
                    Buy Back disponibil
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* ---------- Similar cars ---------- */}
      {similar.length > 0 && (
        <section className="container-wide mt-20 sm:mt-28">
          <div className="mb-8 sm:mb-10">
            <div className="eyebrow">Îți pot plăcea și</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Mașini{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                similare
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {similar.map((c, i) => (
              <CarCard key={c.id} car={c} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* ---------- Final CTA ---------- */}
      <section className="container-wide mt-20 sm:mt-28">
        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#0e0d0b] to-[#0a0a0a] px-6 py-14 sm:px-12 sm:py-20 text-center">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-[#D4AF37]/8 blur-3xl" />
          <div className="relative max-w-2xl mx-auto">
            <div className="eyebrow">Interesat?</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Te interesează această{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                mașină
              </span>
              ?
            </h2>
            <p className="mt-4 text-white/60 text-base sm:text-lg leading-relaxed">
              Contactează-ne și programează o vizionare sau un test drive.
              Răspundem rapid, fără angajament.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5">
              <a
                href={`tel:${phoneClean}`}
                className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-7 py-3.5 font-bold text-[12px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]"
              >
                <Phone className="h-4 w-4" />
                Sună · {settings.phone}
              </a>
              {settings.phone_secondary && (
                <a
                  href={`tel:${settings.phone_secondary.replace(/\s/g, "")}`}
                  data-testid="car-detail-cta-phone-alt"
                  className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-7 py-3.5 font-bold text-[12px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]"
                >
                  <Phone className="h-4 w-4" />
                  Sună · {settings.phone_secondary}
                </a>
              )}
              <a
                href={`https://wa.me/40${wa.replace(/^0/, "")}?text=${waText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full px-7 py-3.5 font-bold text-[12px] uppercase tracking-[0.18em] transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
              <button
                onClick={() => openForm("mesaj")}
                className="inline-flex items-center gap-2 bg-transparent border border-white/25 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white rounded-full px-7 py-3.5 font-bold text-[12px] uppercase tracking-[0.18em] transition-colors"
              >
                <Send className="h-4 w-4" />
                Trimite mesaj
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Mobile sticky action bar ---------- */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/[0.08] p-3"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-2">
          <a
            href={`tel:${phoneClean}`}
            className="flex-1 h-12 inline-flex items-center justify-center gap-1.5 bg-[#D4AF37] active:bg-[#B5952F] text-[#0a0a0a] rounded-full font-bold text-[12px] uppercase tracking-[0.14em]"
            aria-label="Sună acum"
          >
            <Phone className="h-4 w-4" />
            Sună
          </a>
          <a
            href={`https://wa.me/40${wa.replace(/^0/, "")}?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 h-12 inline-flex items-center justify-center gap-1.5 bg-[#25D366] active:bg-[#1EBE5A] text-white rounded-full font-bold text-[12px] uppercase tracking-[0.14em]"
            aria-label="WhatsApp"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
          <button
            onClick={() => openForm("finantare")}
            className="flex-1 h-12 inline-flex items-center justify-center gap-1.5 bg-white/[0.05] border border-white/[0.1] active:bg-white/[0.1] text-white rounded-full font-bold text-[12px] uppercase tracking-[0.14em]"
            aria-label="Ofertă"
          >
            <Calculator className="h-4 w-4 text-[#D4AF37]" />
            Ofertă
          </button>
        </div>
      </div>

      {/* ---------- Fullscreen gallery ---------- */}
      <AnimatePresence>
        {fs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur flex items-center justify-center p-4"
            onClick={() => setFs(false)}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFs(false);
              }}
              className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0a0a0a] hover:border-[#D4AF37] transition-colors"
              aria-label="Închide"
            >
              <X className="h-5 w-5" />
            </button>
            <div
              className="absolute top-4 left-4 bg-black/60 backdrop-blur border border-white/15 rounded-full px-4 py-2 text-[12px] font-bold text-white"
              onClick={(e) => e.stopPropagation()}
            >
              {idx + 1} / {imgs.length}
            </div>
            <div
              className="relative max-h-full max-w-6xl w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <SmartImage
                src={imgs[idx]}
                context="full"
                placeholder={false}
                alt=""
                className="max-h-[92vh] max-w-full object-contain"
              />
            </div>
            {imgs.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIdx((i) => (i - 1 + imgs.length) % imgs.length);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0a0a0a] hover:border-[#D4AF37] transition-colors"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIdx((i) => (i + 1) % imgs.length);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0a0a0a] hover:border-[#D4AF37] transition-colors"
                  aria-label="Următor"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Inquiry modal ---------- */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0f0f0f] border border-white/[0.08] rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-6 sm:p-8"
              style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="eyebrow">
                    {formIntent === "rezervare"
                      ? "Rezervare"
                      : formIntent === "mesaj"
                        ? "Trimite mesaj"
                        : "Ofertă finanțare"}
                  </div>
                  <h3 className="mt-2 font-display text-2xl font-extrabold text-white tracking-tight">
                    {car.brand} {car.model}
                  </h3>
                  <p className="mt-1 text-sm text-white/50">
                    {formatPrice(car.price)}
                  </p>
                </div>
                <button
                  onClick={() => setShowForm(false)}
                  className="h-10 w-10 rounded-full border border-white/[0.1] hover:border-[#D4AF37]/50 text-white flex items-center justify-center transition-colors"
                  aria-label="Închide"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <InquiryForm
                intent={formIntent}
                form={form}
                setForm={setForm}
                onSubmit={submitInquiry}
                submitting={submitting}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Rate calculator component ---------- */
function RateCalculator({ price, onRequest }) {
  const [advance, setAdvance] = useState(Math.round(price * 0.2));
  const [months, setMonths] = useState(60);
  const rate = useMemo(
    () => computeMonthlyRate(price, advance, months),
    [price, advance, months],
  );

  const advancePct = price > 0 ? Math.round((advance / price) * 100) : 0;

  return (
    <div className="relative rounded-3xl overflow-hidden border border-white/[0.06] bg-[#111111] p-6 sm:p-8">
      <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-[#D4AF37]" />
          <div className="eyebrow !text-[#D4AF37]">Calculator rată</div>
        </div>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          Estimează rata{" "}
          <span className="font-serif-italic gold-gradient-text font-medium">
            lunară
          </span>
        </h2>
        <p className="mt-2 text-white/50 text-sm max-w-md">
          Estimare orientativă. Oferta finală se personalizează prin partenerii
          noștri financiari.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-white/50 font-bold">
                <span>Avans</span>
                <span className="text-[#D4AF37]">
                  {formatPrice(advance)} · {advancePct}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={Math.round(price * 0.9)}
                step={100}
                value={advance}
                onChange={(e) => setAdvance(parseInt(e.target.value))}
                className="mt-3 w-full accent-[#D4AF37]"
              />
            </div>
            <div>
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-white/50 font-bold">
                <span>Perioadă</span>
                <span className="text-[#D4AF37]">{months} luni</span>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {[24, 36, 48, 60].map((m) => (
                  <button
                    key={m}
                    onClick={() => setMonths(m)}
                    className={`h-10 rounded-lg text-[13px] font-bold border transition-colors ${months === m
                        ? "bg-[#D4AF37] border-[#D4AF37] text-[#0a0a0a]"
                        : "border-white/[0.1] text-white/80 hover:border-[#D4AF37]/50"
                      }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="relative rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] p-6">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold">
              Rată lunară estimată
            </div>
            <div className="mt-2 font-display text-5xl font-extrabold gold-gradient-text leading-none">
              {formatPrice(rate)}
            </div>
            <div className="mt-2 text-[12px] text-white/50">
              /lună · dobândă orientativă 8,5% p.a.
            </div>
            <button
              onClick={onRequest}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-11 font-bold text-[12px] uppercase tracking-[0.16em] transition-colors"
            >
              Solicită ofertă reală
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Inquiry form ---------- */
const inp =
  "bg-[#0a0a0a] border border-white/[0.1] rounded-lg h-11 px-3.5 w-full text-white placeholder-white/40 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[14px] outline-none transition-colors";
const lbl =
  "block text-[10px] font-bold uppercase tracking-[0.16em] text-white/50 mb-1.5";

export function InquiryForm({ intent, form, setForm, onSubmit, submitting }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const showFinance = intent === "finantare";
  const cta =
    intent === "rezervare"
      ? "Rezervă mașina"
      : intent === "mesaj"
        ? "Trimite mesajul"
        : "Trimite solicitarea";

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className={lbl}>Nume și prenume *</label>
        <input
          required
          data-testid={TID.finName}
          className={inp}
          value={form.name}
          onChange={set("name")}
          autoComplete="name"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Telefon *</label>
          <input
            required
            data-testid={TID.finPhone}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            className={inp}
            value={form.phone}
            onChange={set("phone")}
            placeholder="07XX XXX XXX"
          />
        </div>
        <div>
          <label className={lbl}>Email</label>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            data-testid={TID.finEmail}
            className={inp}
            value={form.email}
            onChange={set("email")}
          />
        </div>
      </div>
      {showFinance && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Avans dorit (€)</label>
            <input
              type="number"
              inputMode="numeric"
              data-testid={TID.finAdvance}
              className={inp}
              value={form.advance}
              onChange={set("advance")}
              placeholder="Opțional"
            />
          </div>
          <div>
            <label className={lbl}>Perioadă</label>
            <select
              data-testid={TID.finPeriod}
              className={inp}
              value={form.period_months}
              onChange={set("period_months")}
            >
              <option value={24}>24 luni</option>
              <option value={36}>36 luni</option>
              <option value={48}>48 luni</option>
              <option value={60}>60 luni</option>
            </select>
          </div>
        </div>
      )}
      <div>
        <label className={lbl}>Mesaj</label>
        <textarea
          data-testid={TID.finMessage}
          className={`${inp} h-24 py-3 resize-none`}
          value={form.message}
          onChange={set("message")}
          placeholder={
            intent === "rezervare"
              ? "Doresc să rezerv această mașină..."
              : "Detalii suplimentare (opțional)"
          }
        />
      </div>
      <button
        data-testid={TID.finSubmit}
        disabled={submitting}
        className="w-full h-12 bg-[#D4AF37] hover:bg-[#e8c86a] active:bg-[#B5952F] disabled:opacity-60 text-[#0a0a0a] rounded-full font-bold text-[13px] uppercase tracking-[0.16em] transition-colors shadow-[0_10px_30px_-8px_rgba(212,175,55,0.5)]"
      >
        {submitting ? "Se trimite..." : cta}
      </button>
      <p className="text-[11px] text-white/40 text-center">
        Fără spam. Te contactăm în cel mai scurt timp posibil.
      </p>
    </form>
  );
}

/* Alias for backward-compat with old import name */
export const FinancingForm = InquiryForm;
